'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { label: 'The Day', href: '/wedding-day-schedule', key: 'nav-schedule' },
  { label: 'Theme', href: '/theme', key: 'nav-theme' },
  { label: 'RSVP', href: '/rsvp-page', key: 'nav-rsvp' },
  { label: 'Accommodation and Activities', href: '/places-to-stay-things-to-do', key: 'nav-dartmouth' },
  { label: 'Gallery', href: '/photo-gallery', key: 'nav-gallery' },
  { label: 'Gift', href: '/honeymoon-fund', key: 'nav-gift' },
  { label: 'FAQ', href: '/faq', key: 'nav-faq' },
];

export default function WeddingNav({ transparent = false }: { transparent?: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTransparent = transparent && !scrolled && !mobileOpen;

  // Build FR equivalent path
  const frPath = (() => {
    if (pathname === '/wedding-day-schedule') return '/fr/schedule';
    if (pathname === '/rsvp-page') return '/fr/rsvp';
    if (pathname === '/places-to-stay-things-to-do') return '/fr/dartmouth';
    if (pathname === '/honeymoon-fund') return '/fr/gift';
    if (pathname === '/faq') return '/fr/faq';
    if (pathname === '/theme') return '/fr/theme';
    if (pathname === '/landing-page') return '/fr';
    return '/fr';
  })();

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-600 ${
          isTransparent
            ? 'bg-transparent' :'bg-buttermilk/95 backdrop-blur-sm border-b border-border'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 h-16 flex items-center justify-between">
          {/* Monogram */}
          <Link
            href="/wedding-day-schedule"
            className="font-serif text-xl tracking-[0.15em] transition-opacity duration-300 hover:opacity-70 whitespace-nowrap"
            style={{ color: isTransparent ? '#FAF8F3' : '#2C3E50' }}
          >
            G & B
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                href={link.href}
                className={`text-xs tracking-[0.25em] uppercase font-sans font-400 transition-all duration-300 ${
                  pathname === link.href
                    ? isTransparent
                      ? 'opacity-100 border-b border-current pb-0.5' :'opacity-100 border-b border-faded-navy pb-0.5' :'opacity-60 hover:opacity-100'
                }`}
                style={{ color: isTransparent ? '#FAF8F3' : '#2C3E50' }}
              >
                {link.label}
              </Link>
            ))}
            {/* Language toggle */}
            <Link
              href={frPath}
              className="opacity-60 hover:opacity-100 transition-all duration-300 border border-current px-2 py-0.5 text-base leading-none"
              style={{ color: isTransparent ? '#FAF8F3' : '#2C3E50' }}
              title="Français"
            >
              🇫🇷
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden transition-opacity duration-300 hover:opacity-70"
            style={{ color: isTransparent ? '#FAF8F3' : '#2C3E50' }}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-40 bg-buttermilk flex flex-col items-center justify-center transition-all duration-600 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="font-serif text-3xl tracking-[0.2em] text-faded-navy mb-16">G & B</div>
        <div className="flex flex-col items-center gap-10">
          {navLinks.map((link) => (
            <Link
              key={`mobile-${link.key}`}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className={`font-serif text-2xl tracking-[0.1em] text-faded-navy transition-opacity duration-300 text-center ${
                pathname === link.href ? 'opacity-100' : 'opacity-50 hover:opacity-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            href={frPath}
            onClick={() => setMobileOpen(false)}
            className="text-2xl text-faded-navy opacity-50 hover:opacity-100 transition-opacity duration-300 border border-faded-navy/30 px-4 py-1"
            title="Français"
          >
            🇫🇷
          </Link>
        </div>
        <div className="mt-20 text-xs tracking-[0.3em] uppercase font-sans text-stone text-center">
          XXIX · VIII · MMXXVI
        </div>
      </div>
    </>
  );
}